import {Dealership, DealershipContact, SearchDomain} from "@/types";
import {API} from "./environment";

async function requestAPI(body: any, method: string, endpoint: string, contentType: string = "application/json") {
    const headers = new Headers();

    // Set content type if provided
    if (contentType) {
        headers.append("Content-Type", contentType);
    }

    // Create object specifying request properties
    const requestOptions: any = {
        method,
        credentials: "include",
        headers
    };

    // Only set the body if not null (Edge complains if the request method is GET with a body)
    if (body) {
        requestOptions.body = body;
    }

    const response = await fetch(`${API}/${endpoint}`, requestOptions);

    const json = await response.json();

    return response.ok ? json : Promise.reject(json);
}

///////////// USER //////////////////////
export const getUserInformation = () => {
    return requestAPI(null, "GET", "user/info");
};

export const performLogin = (payload: string) => {
    return requestAPI(payload, "POST", "user/login");
};

export const getUserList = (params: string) => {
    return requestAPI(null, "GET", `user/list?${params.toString()}`);
};

export const getAvailablePermissions = () => {
    return requestAPI(null, "GET", `user/permissions`);
};

export const getAssociatedPermissions = (id: number) => {
    return requestAPI(null, "GET", `user/${id}/permissions`);
};

export const getAssociatedDealerships = (id: number) => {
    return requestAPI(null, "GET", `user/${id}/dealerships`);
};

export const getAssociatedAutoGroups = (id: number) => {
    return requestAPI(null, "GET", `user/${id}/auto-groups`);
};

export const getAssociatedAreas = (id: number) => {
    return requestAPI(null, "GET", `user/${id}/areas`);
};

export const createUser = (body: string) => {
    return requestAPI(body, "POST", `user`);
};

export const updateUser = (body: string, id: number) => {
    return requestAPI(body, "PUT", `user/${id}`);
};

export const deleteUser = (id: number) => {
    return requestAPI(null, "DELETE", `user/${id}`);
};

export const logout = () => {
    return requestAPI(null, "GET", `user/logout`);
};

///////////// SEARCH ////////////////////
export const searchByArea = (term: string) => {
    return requestAPI(null, "GET", `search/area?term=${term}`);
};

export const searchByDealership = (term: string) => {
    return requestAPI(null, "GET", `search/dealership?term=${term}`);
};

export const searchByDomainName = (term: string) => {
    return requestAPI(null, "GET", `search/domain?term=${term}`);
};

export const searchByAutoGroup = (term: string) => {
    return requestAPI(null, "GET", `search/dealership-group?term=${term}`);
};

export const searchByUser = (term: string) => {
    return requestAPI(null, "GET", `search/user?term=${term}`);
};

export const searchByEmail = (term: string) => {
    return requestAPI(null, "GET", `search/email?term=${term}`);
};

///////////// MAP ///////////////////////
export const getMapMarkers = (params?: string): Promise<Dealership[]> => {
    return requestAPI(null, "GET", `map?${params}`)
};

///////////// DEALERSHIP ////////////////
export const getDealerInformation = (uri: string) => {
    return requestAPI(null, "GET", `dealership/${uri}`);
};

export const getVehicleCountForLastThreeMonthsForDealer = (id: number) => {
    return requestAPI(null, "GET", `dealership/${id}/recent`);
};

export const getDealershipContacts = (id: number): Promise<DealershipContact[]> => {
    return requestAPI(null, "GET", `dealership/${id}/contact`);
};

export const deleteDealershipContact = (dealershipId: number, contactId: number): Promise<DealershipContact[]> => {
    return requestAPI(null, "DELETE", `dealership/${dealershipId}/contact/${contactId}`);
};

export const listDealerships = (params?: string) => {
    return requestAPI(null, "GET", `dealership/list?${params.toString()}`);
};

export const addDealership = (payload: string) => {
    return requestAPI(payload, "POST", `dealership`);
};

export const updateDealershipInformation = (payload: string, dealershipId: number) => {
    return requestAPI(payload, "PUT", `dealership/${dealershipId}`);
};

export const getDealershipSearchPaths = (dealershipId: number) => {
    return requestAPI(null, "GET", `dealership/${dealershipId}/search-paths`);
};

export const deleteDealership = (dealershipId: number) => {
    return requestAPI(null, "DELETE", `dealership/${dealershipId}`);
};

export const deleteDomain = (domainId: number) => {
    return requestAPI(null, "DELETE", `dealership/domain/${domainId}`);
};

export const getSearchDomains = (params?: string): Promise<SearchDomain[]> => {
    return requestAPI(null, "GET", `dealership/domain/list?${params.toString()}`);
};

export const addSearchDomain = (body: string) => {
    return requestAPI(body, "POST", `dealership/domain`);
};

export const addDealershipContact = (dealershipId: number, body: string) => {
    return requestAPI(body, "POST", `dealership/${dealershipId}/contact`);
};

export const sendDealershipVehicleEmailForDateRange = (dealershipId: number, body: string) => {
    return requestAPI(body, "POST", `dealership/${dealershipId}/email`);
};

export const transferVehicleHistoryLogsToDealership = (dealershipId: number, body: string) => {
    return requestAPI(body, "PUT", `dealership/${dealershipId}/transfer`);
};

///////////// VEHICLE ///////////////////
export const getMostRecentAccidentVehicles = () => {
    return requestAPI(null, "GET", `vehicle/recent?filter=accident`);
};

export const getVehicleHistoryForDateRange = (id: number, params: string) => {
    return requestAPI(null, "GET", `dealership/${id}/history?${params}`);
};

export const getVehicleHistoryLogs = (params: string) => {
    return requestAPI(null, "GET", `vehicle/logs?${params}`);
};

export const createVehicleHistoryLog = (dealershipId: number, body: string) => {
    return requestAPI(body, "POST", `dealership/${dealershipId}/logs`);
};

export const updateVehicleLogPreInspectionResponse = (vehicleLogId: number, body: string) => {
    return requestAPI(body, "PUT", `vehicle/log/${vehicleLogId}/pre-inspection-response`);
};

///////////// AREA ///////////////////
export const getAreaList = (params: string) => {
    return requestAPI(null, "GET", `area/list?${params}`);
};

export const addArea = (body: string) => {
    return requestAPI(body, "POST", `area`);
};

export const getAssociatedDealershipAreas = (dealershipId: number) => {
    return requestAPI(null, "GET", `dealership/${dealershipId}/areas`);
};

export const deleteArea = (id: number) => {
    return requestAPI(null, "DELETE", `area/${id}`);
};

///////////// AUTO GROUP ///////////////////
export const getAutoGroupList = (params: string) => {
    return requestAPI(null, "GET", `auto-group/list?${params}`);
};

export const addAutoGroup = (body: string) => {
    return requestAPI(body, "POST", `auto-group`);
};

export const getAssociatedAutoGroup = (dealershipId: number) => {
    return requestAPI(null, "GET", `dealership/${dealershipId}/auto-groups`);
};

export const deleteAutoGroup = (autoGroupId: number) => {
    return requestAPI(null, "DELETE", `auto-group/${autoGroupId}`);
};

/////////////// QUEUE //////////////////////

export const getDealershipRunQueue = () => {
    return requestAPI(null, "GET", `queue`);
};

export const insertDealershipRunQueue = (payload: string) => {
    return requestAPI(payload, "POST", `queue`);
};

export const insertScheduleTask = (payload: string) => {
    return requestAPI(payload, "POST", `queue/schedule`);
};

export const updateScheduleTask = (payload: string, id: number) => {
    return requestAPI(payload, "PUT", `queue/schedule/${id}`);
};

export const getScheduleTasks = () => {
    return requestAPI(null, "GET", `queue/schedule`);
};

export const deleteScheduleTask = (id: number) => {
    return requestAPI(null, "DELETE", `queue/schedule/${id}`);
};

/////////////// APPLICATION //////////////////////

export const listApplicationAccessSessions = () => {
    return requestAPI(null, "GET", `application/access/session`);
};

export const listApplicationAccessPermissions = () => {
    return requestAPI(null, "GET", `application/access/permissions`);
};

export const createNewAccessKey = (body: string) => {
    return requestAPI(body, "POST", `application/access/session`);
};

export const deleteAccessKey = (key: string) => {
    return requestAPI(null, "DELETE", `application/access/session?key=${key}`)
};
